import { useEffect, useMemo, useState, useCallback } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, TrendingUp, XCircle, CheckCircle2, Euro, Clock } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
  PieChart, Pie, Cell, LineChart, Line,
} from "recharts";
import { useOutcomes } from "@/context/OutcomesContext";

const todayIso = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

export default function StatisticsPage() {
  const { outcomes, results, metaFor, resultMetaFor } = useOutcomes();
  const [start, setStart] = useState(daysAgo(30));
  const [end, setEnd] = useState(todayIso());
  const [collab, setCollab] = useState("all");
  const [calendar, setCalendar] = useState("all"); // "all" | "consulenti" | "trainers"
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = { start: `${start}T00:00:00`, end: `${end}T23:59:59` };
      if (collab !== "all") params.collaborator_id = collab;
      if (calendar !== "all") params.calendar = calendar;
      const { data } = await api.get("/stats", { params });
      setStats(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [start, end, collab, calendar]);

  useEffect(() => {
    api.get("/users").then(({ data }) => setUsers(data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams({ start: `${start}T00:00:00`, end: `${end}T23:59:59` });
      if (collab !== "all") params.set("collaborator_id", collab);
      if (calendar !== "all") params.set("calendar", calendar);
      const res = await fetch(`${API}/stats/export?${params.toString()}`, { credentials: "include" });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `statistiche_${calendar === "all" ? "tutti" : calendar}_${start}_${end}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV scaricato");
    } catch (e) { toast.error("Errore export CSV"); }
  };

  const statusData = useMemo(() => {
    if (!stats) return [];
    return Object.entries(stats.by_status).map(([k, v]) => {
      const m = metaFor(k);
      return { name: m.label || k, value: v, color: m.color || "#94A3B8" };
    });
  }, [stats, metaFor]);

  const resultDataConsulenti = useMemo(() => {
    if (!stats?.by_result) return [];
    return Object.entries(stats.by_result)
      .filter(([k]) => k.startsWith("consulenti::"))
      .map(([k, v]) => {
        const key = k.split("::")[1];
        const m = resultMetaFor("consulenti", key);
        return { name: m.label || key, value: v, color: m.color || "#94A3B8" };
      });
  }, [stats, resultMetaFor]);

  const resultDataTrainers = useMemo(() => {
    if (!stats?.by_result) return [];
    return Object.entries(stats.by_result)
      .filter(([k]) => k.startsWith("trainers::"))
      .map(([k, v]) => {
        const key = k.split("::")[1];
        const m = resultMetaFor("trainers", key);
        return { name: m.label || key, value: v, color: m.color || "#94A3B8" };
      });
  }, [stats, resultMetaFor]);

  const collabData = useMemo(() => {
    if (!stats) return [];
    return stats.by_collaborator.map((c) => {
      const row = { name: c.name };
      outcomes.forEach((o) => { row[o.label] = c[o.key] || 0; });
      return row;
    });
  }, [stats, outcomes]);

  return (
    <div className="p-8 h-full overflow-y-auto">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Statistiche</h1>
          <p className="text-muted-foreground text-sm mt-1">Analisi appuntamenti, stati, esiti e performance dei collaboratori.</p>
        </div>
        <Button onClick={exportCsv} data-testid="stats-export-csv-button"><Download className="h-4 w-4 mr-2" />Esporta CSV</Button>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4 bg-card border rounded-lg p-4">
        <div>
          <Label className="text-xs uppercase font-bold tracking-widest text-muted-foreground">Dal</Label>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" data-testid="stats-start-input" />
        </div>
        <div>
          <Label className="text-xs uppercase font-bold tracking-widest text-muted-foreground">Al</Label>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" data-testid="stats-end-input" />
        </div>
        <div>
          <Label className="text-xs uppercase font-bold tracking-widest text-muted-foreground">Consulente</Label>
          <Select value={collab} onValueChange={setCollab}>
            <SelectTrigger className="w-56" data-testid="stats-filter-consultant-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i consulenti</SelectItem>
              {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase font-bold tracking-widest text-muted-foreground">Calendario</Label>
          <div className="flex items-center gap-1 bg-slate-100 border rounded-md p-0.5" data-testid="stats-calendar-toggle">
            {[
              { k: "all",         label: "Tutti" },
              { k: "consulenti",  label: "Consulenti" },
              { k: "trainers",    label: "Trainers" },
            ].map((opt) => (
              <button
                key={opt.k}
                onClick={() => setCalendar(opt.k)}
                data-testid={`stats-calendar-${opt.k}`}
                className={`text-xs font-semibold px-3 h-9 rounded transition-colors ${
                  calendar === opt.k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {stats && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <MetricCard label="Totale" value={stats.total} icon={TrendingUp} testid="stats-metric-card-total" />
            <MetricCard label="Effettuati" value={`${stats.completion_rate}%`} icon={CheckCircle2} accent="text-emerald-600" testid="stats-metric-card-completion" />
            <MetricCard label="No-Show" value={`${stats.no_show_rate}%`} icon={XCircle} accent="text-rose-600" testid="stats-metric-card-noshow" />
            <MetricCard label="Fatturato" value={`€ ${stats.revenue.toFixed(0)}`} icon={Euro} testid="stats-metric-card-revenue" />
            <MetricCard label="Ore erogate" value={`${stats.hours.toFixed(1)}h`} icon={Clock} testid="stats-metric-card-hours" />
          </div>

          <div className="grid lg:grid-cols-2 gap-4 mb-4">
            <Card>
              <CardHeader><CardTitle className="font-display text-lg">Distribuzione Stati</CardTitle></CardHeader>
              <CardContent style={{ height: 300 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} paddingAngle={2}>
                      {statusData.map((s) => <Cell key={s.name} fill={s.color} />)}
                    </Pie>
                    <Legend />
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="font-display text-lg">Trend giornaliero</CardTitle></CardHeader>
              <CardContent style={{ height: 300 }}>
                <ResponsiveContainer>
                  <LineChart data={stats.by_day}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="total" stroke="#2563EB" name="Totale" strokeWidth={2} />
                    <Line type="monotone" dataKey="completed" stroke="#10B981" name="Effettuati" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {(resultDataConsulenti.length > 0 || resultDataTrainers.length > 0) && (
            <div className="grid lg:grid-cols-2 gap-4 mb-4">
              {resultDataConsulenti.length > 0 && (
                <Card>
                  <CardHeader><CardTitle className="font-display text-lg">Esiti — Consulenti</CardTitle></CardHeader>
                  <CardContent style={{ height: 260 }}>
                    <ResponsiveContainer>
                      <BarChart data={resultDataConsulenti}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="value" name="Numero">
                          {resultDataConsulenti.map((s) => <Cell key={s.name} fill={s.color} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
              {resultDataTrainers.length > 0 && (
                <Card>
                  <CardHeader><CardTitle className="font-display text-lg">Esiti — Trainers</CardTitle></CardHeader>
                  <CardContent style={{ height: 260 }}>
                    <ResponsiveContainer>
                      <BarChart data={resultDataTrainers}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="value" name="Numero">
                          {resultDataTrainers.map((s) => <Cell key={s.name} fill={s.color} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          <Card className="mb-4">
            <CardHeader><CardTitle className="font-display text-lg">Appuntamenti per consulente</CardTitle></CardHeader>
            <CardContent style={{ height: 320 }}>
              <ResponsiveContainer>
                <BarChart data={collabData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  {outcomes.map((o) => (
                    <Bar key={o.key} dataKey={o.label} stackId="a" fill={o.color} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function MetricCard({ label, value, icon: Icon, accent = "text-slate-900", testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase font-bold tracking-widest text-muted-foreground">{label}</div>
            <div className={`font-display text-2xl font-bold mt-1 ${accent}`}>{value}</div>
          </div>
          <Icon className="h-5 w-5 text-muted-foreground" />
        </div>
      </CardContent>
    </Card>
  );
}
